import { FC } from 'react';
import { Controller, Form, Resolver, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useNotices } from '@/features/notices/useNotices';
import { useCreateBeneficiaryMutation } from '@/shared/generated/graphql';
import { useFormSave } from '@/shared/lib/hooks/use-form-save';
import {
  EMAIL_REGEX,
  INVALID_EMAIL_ERROR,
  REQUIRED_ERROR,
} from '@/shared/lib/hooks/useFormRenderV2/helpers';
import { BeneficiaryDto, BeneficiaryGroup, CollectionName } from '@/shared/lib/sj-orm';
import { BaseDto } from '@/shared/lib/sj-orm/models/base.dto';
import { Icon } from '@/shared/uikit/Icon';
import { Input } from '@/shared/uikitV2/Input';
import { useTranslation } from '@/shared/utils/i18n/useTranslation';

import { BeneficiaryGroupI18n } from '../BeneficiaryGroupI18n';

import { InputWrap, ValueView } from './styled';

const MAX_LENGTH = 100;

type FastCreateBeneficiaryFormProps = {
  group: BeneficiaryGroup;
  formId: string;
  onClose: () => void;
  onSave?: (dto: BeneficiaryDto) => void;
};

export const FastCreateBeneficiaryFormSchema = yup.object({
  group: yup.string().required(REQUIRED_ERROR),
  email: yup.string().trim().matches(EMAIL_REGEX, INVALID_EMAIL_ERROR).required(REQUIRED_ERROR),
  nickName: yup.string().required(REQUIRED_ERROR),
  firstName: yup.string().required(REQUIRED_ERROR),
  lastName: yup.string().required(REQUIRED_ERROR),
  secondName: yup.string(),
});

type FormType = Pick<
  BeneficiaryDto,
  'group' | 'email' | 'nickName' | 'firstName' | 'lastName' | 'secondName'
>;

export const FastCreateBeneficiaryForm: FC<FastCreateBeneficiaryFormProps> = ({
  group,
  formId,
  onClose,
  onSave,
}) => {
  const { t } = useTranslation('widgets/modals', { keyPrefix: 'fastCreateBeneficiary' });
  const notices = useNotices();
  const [createBeneficiaryMutation] = useCreateBeneficiaryMutation();

  const { onSubmit: formOnSubmit, finish } = useFormSave({
    collectionName: CollectionName.BENEFICIARIES,
    updateOrSave: 'save',
    initialDtoData: { group },
  });

  const { control } = useForm<FormType>({
    defaultValues: { group },
    resolver: yupResolver(FastCreateBeneficiaryFormSchema) as Resolver<FormType>,
  });

  const onSubmit = async (data: FormType) => {
    const dto = { ...data };

    const centralDbBeneficiary = await createBeneficiaryMutation({
      variables: {
        email: data.email,
        dontSendInviteEmail: true,
      },
    });

    if (!centralDbBeneficiary.data?.createBeneficiary.account.id) {
      notices.error(t('errors.createBeneficiary'));
      return;
    }

    const beneficiaryData = {
      centralDbProfileId: centralDbBeneficiary.data?.createBeneficiary.account.id,
      invitationCode: centralDbBeneficiary.data?.createBeneficiary.invitationCode,
      linkedCountryCode: '',
    };

    formOnSubmit({ ...dto, ...beneficiaryData } as unknown as BaseDto);
    const result = await finish({ ...dto, ...beneficiaryData } as unknown as BaseDto);

    if (result?.id) {
      onClose();
      onSave?.(result as BeneficiaryDto);
    } else {
      notices.error(t('errors.createBeneficiary'));
    }
  };

  return (
    <Form
      style={{ display: 'flex', flexDirection: 'column', gap: 24 }}
      control={control}
      onSubmit={({ data }) => onSubmit(data)}
      id={formId}
    >
      <Controller
        control={control}
        name="nickName"
        render={({ field, fieldState }) => (
          <Input
            {...field}
            status={fieldState.error ? 'error' : 'default'}
            errorText={fieldState.error?.message}
            label={t('fields.nickName')}
            maxLength={MAX_LENGTH}
            required
          />
        )}
      />

      <Controller
        control={control}
        name="group"
        render={({ field, fieldState }) => (
          <InputWrap>
            <Input
              {...field}
              errorText={fieldState.error?.message}
              status={fieldState.error ? 'error' : 'default'}
              label={t('fields.group')}
              iconAfter={field.value ? <Icon name="lock" size="s16" /> : null}
              readOnly
              required
            />
            <ValueView>
              <BeneficiaryGroupI18n showIcon group={field.value} />
            </ValueView>
          </InputWrap>
        )}
      />

      <Controller
        control={control}
        name="firstName"
        render={({ field, fieldState }) => (
          <Input
            {...field}
            status={fieldState.error ? 'error' : 'default'}
            errorText={fieldState.error?.message}
            label={t('fields.firstName')}
            maxLength={MAX_LENGTH}
            required
          />
        )}
      />

      <Controller
        control={control}
        name="lastName"
        render={({ field, fieldState }) => (
          <Input
            {...field}
            status={fieldState.error ? 'error' : 'default'}
            errorText={fieldState.error?.message}
            label={t('fields.lastName')}
            maxLength={MAX_LENGTH}
            required
          />
        )}
      />

      <Controller
        control={control}
        name="secondName"
        render={({ field, fieldState }) => (
          <Input
            {...field}
            status={fieldState.error ? 'error' : 'default'}
            errorText={fieldState.error?.message}
            label={t('fields.secondName')}
            maxLength={MAX_LENGTH}
          />
        )}
      />

      <Controller
        control={control}
        name="email"
        render={({ field, fieldState }) => (
          <Input
            {...field}
            type="email"
            status={fieldState.error ? 'error' : 'default'}
            errorText={fieldState.error?.message}
            label={t('fields.email')}
            maxLength={MAX_LENGTH}
            required
          />
        )}
      />
    </Form>
  );
};
